import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TextFields as TextFormatIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  PersonAdd as PersonAddIcon,
  Psychology as PsychologyIcon,
  Mic as MicIcon,
  PresentToAll as PresentToAllIcon,
  Business as BusinessIcon,
  Assignment as ClipboardIcon,
  AssignmentTurnedIn as AssessmentIcon,
  Forum as ForumIcon,
  Handshake as HandshakeIcon,
  Html as HtmlIcon,
  EmojiEvents as EmojiEventsIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import { TimelineEvent, Candidate, Position, Application } from '../../types';
import airtableService from '../../services/airtable';

interface LinkedTimelineEvent extends TimelineEvent {
  candidate?: Candidate;
  position?: Position;
  application?: Application;
}

interface TimelineStep {
  id: number;
  title: string;
  description: string;
  type: 'interview' | 'administrative' | 'assessment' | 'final';
  icon: React.ReactNode;
  completed: boolean;
  duration: number; // relative width in the timeline
}

const CandidateUpdates: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<LinkedTimelineEvent[]>([]);
  const [reportDate, setReportDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Responsive breakpoints
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // 20-step interview process timeline based on candidate_steps.csv
  const interviewTimelineSteps: TimelineStep[] = [
    // Initial Setup Phase
    { id: 1, title: 'Candidate Activated', description: 'Candidate profile activated in system', type: 'administrative', icon: <PersonAddIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 2, title: 'Candidate Screened', description: 'Initial screening and evaluation', type: 'administrative', icon: <PsychologyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 3, title: 'Candidate Interviewed', description: 'Initial candidate interview with M4 team', type: 'administrative', icon: <MicIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 4, title: 'Candidate Presented', description: 'Candidate presented to client organization', type: 'administrative', icon: <PresentToAllIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // ESP Interview Phase
    { id: 5, title: 'ESP Interview 1', description: 'First Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 6, title: 'ESP Interview 2', description: 'Second Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 7, title: 'ESP Interview 3', description: 'Third Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Strategic Interview Phase
    { id: 8, title: '1st Strategic Interview', description: 'First strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 9, title: '2nd Strategic Interview', description: 'Second strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 10, title: '3rd Strategic Interview', description: 'Third strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Assessment Phase
    { id: 11, title: 'Assessment 1', description: 'First comprehensive candidate assessment', type: 'assessment', icon: <AssessmentIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 12, title: 'Assessment 2', description: 'Second comprehensive candidate assessment', type: 'assessment', icon: <AssessmentIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Continued Strategic Interview Phase
    { id: 13, title: '4th Strategic Interview', description: 'Fourth strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 14, title: '5th Strategic Interview', description: 'Fifth strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 15, title: '6th Strategic Interview', description: 'Sixth strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 16, title: '7th Strategic Interview', description: 'Seventh strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 17, title: '8th Strategic Interview', description: 'Eighth strategic stakeholder interview', type: 'interview', icon: <ClipboardIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Final Decision Phase
    { id: 18, title: 'Post Interviews Debrief', description: 'Comprehensive team debrief after all interviews', type: 'final', icon: <ForumIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 19, title: 'Prefinal Meeting', description: 'Pre-final decision and evaluation meeting', type: 'final', icon: <HandshakeIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 20, title: 'Final Meeting', description: 'Final decision and offer meeting', type: 'final', icon: <EmojiEventsIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all required data
      const [
        timelineResponse,
        candidatesResponse,
        positionsResponse,
        applicationsResponse
      ] = await Promise.all([
        airtableService.getTimelineEvents({ limit: 50, page: 1 }),
        airtableService.getCandidates(),
        airtableService.getPositions(),
        airtableService.getApplications()
      ]);

      // Handle potential errors
      if (!timelineResponse.success) {
        setError(timelineResponse.error || 'Failed to fetch timeline events');
        return;
      }


      const candidates = candidatesResponse.success ? candidatesResponse.data : [];
      const positions = positionsResponse.success ? positionsResponse.data : [];
      const applications = applicationsResponse.success ? applicationsResponse.data : [];

      // Debug logging for Show in UI filter
      console.log('=== CANDIDATE UPDATES - Show in UI Debug ===');
      console.log('Total candidates fetched:', candidates.length);
      candidates.forEach(c => {
        console.log(`Candidate: ${c.firstName} ${c.lastName}, Show in UI: ${c.showInUI}`);
      });

      // Link timeline events to related data
      const linkedTimelineEvents: LinkedTimelineEvent[] = timelineResponse.data
        .map(event => {
          const application = applications.find(a => a.id === event.applicationId);
          const candidate = candidates.find(c => c.id === application?.candidateId);
          const position = positions.find(p => p.id === application?.positionId);

          return {
            ...event,
            application,
            candidate,
            position
          } as LinkedTimelineEvent;
        })
        .filter(event => {
          // Only show events where candidate exists (i.e., showInUI = true)
          const hasCandidate = event.candidate !== undefined;
          if (!hasCandidate) {
            console.log('Filtering out timeline event - candidate not found or hidden:', event.id);
          }
          return hasCandidate;
        });

      console.log('Timeline events after candidate filter:', linkedTimelineEvents.length);

      setTimelineEvents(linkedTimelineEvents);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch candidate updates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    window.print();
  };

  const generateCandidateUpdatesText = () => {
    let textContent = `CANDIDATE UPDATES REPORT\n`;
    textContent += `Report Date: ${reportDate}\n`;
    textContent += `Generated: ${new Date().toLocaleDateString()}\n`;
    textContent += `Total Updates: ${timelineEvents.length}\n`;
    textContent += `\n${'='.repeat(60)}\n\n`;

    timelineEvents.forEach((event, index) => {
      textContent += `UPDATE ${index + 1}\n`;
      textContent += `${'-'.repeat(20)}\n`;

      // Candidate Information
      const candidateName = event.candidate
        ? `${event.candidate.firstName} ${event.candidate.lastName}`
        : 'Candidate TBD';

      textContent += `Candidate: ${candidateName}\n`;

      // Position Information
      if (event.position) {
        textContent += `Position: ${event.position.title}\n`;
        if (event.candidate?.currentCompany || event.position.company?.name) {
          textContent += `Company: ${event.candidate?.currentCompany || event.position.company?.name}\n`;
        }
      }

      // Event Details
      textContent += `Event Type: ${event.eventType}\n`;
      textContent += `Date: ${event.eventDate.toLocaleDateString()}`;
      if (event.eventTime) {
        textContent += ` at ${event.eventTime}`;
      }
      textContent += `\n`;

      // Current Step Information
      if (event.currentStep) {
        const stepNumber = parseStepNumber(event.currentStep);
        const stepInfo = getTimelineStepByNumber(stepNumber);
        textContent += `Current Step: ${stepNumber}/20`;
        if (stepInfo) {
          textContent += ` - ${stepInfo.title}`;
        }
        textContent += `\n`;
      }

      // Status Change Information
      if (event.previousStatus && event.newStatus) {
        textContent += `Status Change: ${event.previousStatus} → ${event.newStatus}\n`;
      }

      textContent += `Description: ${event.description}\n`;

      if (event.notes) {
        textContent += `Notes: ${event.notes}\n`;
      }

      if (event.nextSteps) {
        textContent += `Next Steps: ${event.nextSteps}\n`;
      }

      if (event.availableMeetTimes) {
        textContent += `Available Meet Times: ${event.availableMeetTimes}\n`;
      }

      textContent += `Created By: ${event.createdBy}\n`;

      // Contact Information for follow-up
      if (event.candidate?.email) {
        textContent += `Candidate Email: ${event.candidate.email}\n`;
      }
      if (event.candidate?.phone) {
        textContent += `Candidate Phone: ${event.candidate.phone}\n`;
      }
      if (event.candidate?.linkedinProfile) {
        textContent += `LinkedIn Profile: ${event.candidate.linkedinProfile}\n`;
      }

      textContent += `\n${'='.repeat(40)}\n\n`;
    });

    return textContent;
  };

  const handleTextFormat = () => {
    const textContent = generateCandidateUpdatesText();

    // Create a new window with the plain text content
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Candidate Updates - Basic Text Format</title>
            <style>
              body {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.4;
                padding: 20px;
                background: white;
                color: black;
                max-width: 800px;
              }
              pre {
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            </style>
          </head>
          <body>
            <pre>${textContent}</pre>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleHTMLFormat = () => {
    let htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Candidate Updates - Email Format</title>
  <style>
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 1.3;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 10px;
      background-color: #f5f5f5;
    }
    .header {
      background-color: #1e40af;
      color: white;
      padding: 8px 12px;
      border-radius: 4px 4px 0 0;
      margin-bottom: 0;
    }
    .header h1 {
      margin: 0;
      font-size: 18px;
    }
    .header .meta {
      font-size: 10px;
      opacity: 0.9;
      margin-top: 2px;
    }
    .update-card {
      background: white;
      border: 2px solid #cbcbcbff;
      border-radius: 4px;
      padding: 8px;
      margin-bottom: 6px;
      box-shadow: 0 1px 2px rgba(0,0,0,0.06);
    }
    .update-header {
      border-bottom: 1px solid #3b82f6;
      padding-bottom: 4px;
      margin-bottom: 5px;
    }
    .candidate-name {
      font-size: 15px;
      font-weight: bold;
      color: #1e40af;
      margin: 0 0 2px 0;
    }
    .position-title {
      color: #6b7280;
      font-size: 12px;
      margin: 0;
    }
    .event-type {
      display: inline-block;
      background-color: #dbeafe;
      color: #1e40af;
      padding: 1px 6px;
      border-radius: 8px;
      font-size: 10px;
      font-weight: 600;
      margin-top: 3px;
    }
    .detail-row {
      margin: 3px 0;
      padding: 2px 0;
    }
    .detail-label {
      font-weight: 600;
      color: #374151;
      display: inline-block;
      min-width: 120px;
      font-size: 11px;
    }
    .detail-value {
      color: #2f3a52ff;
      font-size: 13px;
    }
    .notes-section {
      background-color: #f9fafb;
      padding: 6px;
      border-left: 2px solid #3b82f6;
      margin: 5px 0;
      border-radius: 2px;
    }
    .notes-label {
      font-weight: 600;
      color: #1e40af;
      margin-bottom: 2px;
      font-size: 11px;
    }
    .footer {
      text-align: center;
      padding: 8px;
      color: #6b7280;
      font-size: 10px;
      border-top: 1px solid #e5e7eb;
      margin-top: 10px;
    }
    .step-badge {
      background-color: #fef3c7;
      color: #92400e;
      padding: 1px 6px;
      border-radius: 2px;
      font-weight: 600;
      font-size: 11px;
    }
    .position-group {
      margin-bottom: 12px;
    }
    .position-header {
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
      color: white;
      padding: 6px 12px;
      margin: 8px 0 5px 0;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
      border-left: 3px solid #60a5fa;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>📋 Candidate Updates Report</h1>
    <div class="meta">
      Report Date: ${reportDate} | Generated: ${new Date().toLocaleDateString()} | Total Updates: ${timelineEvents.length}
    </div>
  </div>
`;

    // Group timeline events by position
    const groupedByPosition: { [key: string]: typeof timelineEvents } = {};

    timelineEvents.forEach((event) => {
      const positionKey = event.position?.title || 'No Position Assigned';
      if (!groupedByPosition[positionKey]) {
        groupedByPosition[positionKey] = [];
      }
      groupedByPosition[positionKey].push(event);
    });

    // Generate HTML for each position group
    Object.entries(groupedByPosition).forEach(([positionTitle, events]) => {
      const firstEvent = events[0];
      const companyName = firstEvent.position?.company?.name || '';

      htmlContent += `
  <div class="position-group">
    <div class="position-header">
      📋 POSITION: ${positionTitle}${companyName ? ` at ${companyName}` : ''}
      <span style="float: right; font-size: 13px; opacity: 0.9;">(${events.length} update${events.length !== 1 ? 's' : ''})</span>
    </div>
`;

      events.forEach((event, index) => {
        const candidateName = event.candidate
          ? `${event.candidate.firstName} ${event.candidate.lastName}`
          : 'Candidate TBD';

        htmlContent += `
    <div class="update-card">
      <div class="update-header">
        <h2 class="candidate-name">${candidateName}</h2>
        <span class="event-type">${event.eventType}</span>
      </div>

    <div class="detail-row">
      <span class="detail-label">Interview Date:</span>
      <span class="detail-value" style="font-size: 14px;"><strong>${event.eventDate.toLocaleDateString()}${event.eventTime ? ` at ${event.eventTime}` : ''}</strong></span>
    </div>
`;

      if (event.currentStep) {
        const stepNumber = parseStepNumber(event.currentStep);
        const stepInfo = getTimelineStepByNumber(stepNumber);
        htmlContent += `
    <div class="detail-row">
      <span class="detail-label">Current Step:</span>
      <span class="step-badge" style="font-size: 14px;">Step ${stepNumber} of 20${stepInfo ? ` - ${stepInfo.title}` : ''}</span>
    </div>
`;
      }

      if (event.previousStatus && event.newStatus) {
        htmlContent += `
    <div class="detail-row">
      <span class="detail-label">🔄 Status Change:</span>
      <span class="detail-value">${event.previousStatus} → ${event.newStatus}</span>
    </div>
`;
      }

      htmlContent += `
    <div class="detail-row">
      <span class="detail-label">Client Weigh-in:</span>
      <span class="detail-value">${event.description}</span>
    </div>
`;

      if (event.notes) {
        htmlContent += `
    <div class="notes-section">
      <div class="notes-label">✅ Completed Steps:</div>
      <div>${event.notes.replace(/\n/g, '<br>')}</div>
    </div>
`;
      }

      if (event.nextSteps) {
        htmlContent += `
    <div class="notes-section">
      <div class="notes-label">🎯 Next Steps:</div>
      <div>${event.nextSteps.replace(/\n/g, '<br>')}</div>
    </div>
`;
      }

      if (event.availableMeetTimes) {
        htmlContent += `
    <div class="detail-row">
      <span class="detail-label">Available Meet Times:</span>
      <span class="detail-value" >${event.availableMeetTimes}</span>
    </div>
`;
      }

//       htmlContent += `
//     <div class="detail-row">
//       <span class="detail-label">👤 Created By:</span>
//       <span class="detail-value">${event.createdBy}</span>
//     </div>
// `;

      if (event.candidate?.email || event.candidate?.phone || event.candidate?.linkedinProfile) {
        htmlContent += `
    <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
      <div class="detail-label" style="display: block; margin-bottom: 8px;">Contact Information:</div>
`;
        if (event.candidate?.email) {
          htmlContent += `      <div class="detail-value">✉️ <a href="mailto:${event.candidate.email}">${event.candidate.email}</a></div>\n`;
        }
        if (event.candidate?.phone) {
          htmlContent += `      <div class="detail-value">📱 ${event.candidate.phone}</div>\n`;
        }
        if (event.candidate?.linkedinProfile) {
          htmlContent += `      <div class="detail-value">🔗 <a href="${event.candidate.linkedinProfile}" target="_blank">LinkedIn Profile</a></div>\n`;
        }
        htmlContent += `    </div>\n`;
      }

      htmlContent += `    </div>\n\n`;
      });

      htmlContent += `  </div>\n\n`;
    });

    htmlContent += `
  <div class="footer">
    <p>M4 Consulting - Candidate Updates Report</p>
    <p>This report contains confidential information</p>
  </div>
</body>
</html>`;

    // Create a new window with the HTML content
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    }
  };

  const generatePortalInstructions = () => {
    const baseInstructions = `# M4 Consulting Candidate Progress Portal - Access Instructions

## Welcome to Your Personal Interview Progress Portal

M4 Consulting has created a secure, personalized portal where you can track your interview progress in real-time. This guide will walk you through accessing and using your personal profile page.

---

## 🌐 How to Access Your Profile

### Step 1: Visit Your Personal URL
Your unique profile page can be accessed at any of these domains using your name:

**Format:** \`[domain]/[firstname][lastname]\` (no spaces, lowercase)

**Available Domains:**
- \`https://m4.freeweb3.com/[firstname][lastname]\`
- \`https://m4tracker.com/[firstname][lastname]\`
- \`https://rra.m4tracker.com/[firstname][lastname]\`

### Example URLs for Each Candidate:

`;

    // Get unique candidates from timeline events
    const uniqueCandidates = Array.from(
      new Map(
        timelineEvents
          .filter(event => event.candidate && event.candidate.firstName && event.candidate.lastName)
          .map(event => [event.candidate!.id, event.candidate!])
      ).values()
    );

    // Generate personalized URLs for each candidate
    const candidateUrls = uniqueCandidates.map(candidate => {
      const nameSlug = `${candidate.firstName}${candidate.lastName}`.toLowerCase().replace(/\s+/g, '');
      return `- **${candidate.firstName} ${candidate.lastName}**:
  - **Email**: ${candidate.email || 'Not provided'}
  - **Access Passcode**: ${candidate.accessPasscode || candidate.accessCode || 'Not provided'}
  - **Profile URLs**:
    - \`https://m4tracker.com/${nameSlug}\`
    - \`https://m4.freeweb3.com/${nameSlug}\`
    - \`https://rra.m4tracker.com/${nameSlug}\``;
    }).join('\n\n');

    const remainingInstructions = `

---

## 🔐 Logging Into Your Profile

### Step 2: Enter Your Credentials
When you visit your profile URL, you'll see a secure login screen with:

1. **Your Name**: Confirming this is your profile
2. **Email Address**: Empty field - you must enter your registered email
3. **Access Passcode Field**: Empty field - enter your unique passcode

### Step 3: Authentication Process
- **Email Address**: Enter the email address you registered with M4 Consulting
- **Access Passcode**: Enter the unique passcode provided by your M4 Consulting representative
- **Password Visibility**: Click the eye icon (👁️) next to the passcode field to show/hide your passcode as you type
- Click "Access Profile" to continue

### Security Features:
- ✅ Empty login fields protect your privacy - no information is pre-filled
- ✅ Only you can access your profile with your unique email and passcode combination
- ✅ Your data is completely private and isolated from other candidates
- ✅ Secure encryption protects all information
- ✅ Account access is only available when your status is active

---

## 📊 Navigating Your Progress Portal

### What You'll See:

#### **1. Current Status Overview**
- **Progress Bar**: Visual indicator of your interview completion (Step X of 20)
- **Current Step**: Your current position in the 20-step interview process
- **Status Badge**: Your current application status (Active, In Process, etc.)

#### **2. Interview Progress Timeline**
- **20-Step Process**: Complete visual timeline of the interview journey
- **Step Types**:
  - ⚫ **Administrative Steps**: Initial setup, screening, and presentation (Steps 1-4)
  - 🔵 **Interview Steps**: ESP and Strategic interviews with client stakeholders (Steps 5-17)
  - 🟡 **Final Decision**: Debrief, evaluation, and final decision meetings (Steps 18-20)

#### **3. Your Candidacy Progress**
- **Timeline Events**: Chronological list of all your interview activities
- **Status Changes**: Track when your status was updated
- **Updates & Notes**: Important information and feedback from your interviews
- **Dates & Times**: Complete history of all interview-related activities

### Page Features:

#### **🖨️ Print Your Progress**
- Click the "Print" button to create a PDF of your current progress
- Perfect for your records or sharing with references

#### **🔄 Refresh Data**
- Click the "Refresh" button to get the latest updates
- Your progress is updated in real-time as interviews are completed

#### **🚪 Secure Logout**
- Click "Logout" when finished to securely end your session
- Always logout when using shared computers

---

## 🆘 Troubleshooting

### Can't Find Your Profile?
- **Check Your URL**: Ensure your first and last name are spelled correctly with no spaces
- **Try Different Domains**: If one domain isn't working, try another from the list above
- **Contact Support**: Reach out to your M4 Consulting representative

### Login Issues?
- **Verify Email**: Ensure you're using the email address registered with M4 Consulting
- **Check Passcode**: Verify your access passcode with your M4 representative
- **Use Eye Icon**: Click the eye icon to reveal your passcode and ensure you're typing it correctly
- **Account Status**: Your account must be active to access the portal

### Technical Issues?
- **Try Different Browser**: Chrome, Firefox, Safari, or Edge
- **Clear Cache**: Clear your browser cache and cookies
- **Mobile Access**: The portal works on all devices - try your phone or tablet

---

## 📱 Mobile Access

Your progress portal is fully optimized for mobile devices:
- **Responsive Design**: Works perfectly on phones and tablets
- **Touch-Friendly**: Easy navigation on touchscreen devices
- **Same Features**: All desktop features available on mobile

---

## 🔒 Privacy & Security

Your privacy and data security are our top priorities:

- **No Pre-filled Information**: Your email and passcode fields are always empty when you visit your URL
- **Personal Access Only**: Only you can access your profile with your unique credentials
- **Data Isolation**: You can only see your own interview information
- **Secure Connection**: All data is transmitted using industry-standard encryption
- **No Data Sharing**: Your information is never shared with other candidates or third parties

---

## 📞 Support & Assistance

### Need Help?
If you experience any issues accessing or using your progress portal:

1. **Contact Your M4 Representative**: Reach out to the M4 Consulting representative managing your application
2. **Email Support**: Include your full name and the issue you're experiencing
3. **Phone Support**: Call during business hours for immediate assistance

### Common Questions:

**Q: How often is my progress updated?**
A: Your progress is updated in real-time as interviews are completed and status changes occur.

**Q: Can I access my profile from multiple devices?**
A: Yes! You can log in from any device - computer, phone, or tablet.

**Q: What if I forget my access passcode?**
A: Contact your M4 Consulting representative to have your passcode reset securely.

**Q: Is my information secure?**
A: Absolutely. We use bank-level security to protect your data and ensure complete privacy.

---

## 🎯 Making the Most of Your Portal

### Best Practices:
- **Check Regularly**: Log in weekly to stay updated on your progress
- **Print Records**: Save PDF copies of your progress for your records
- **Mobile Bookmarks**: Bookmark your profile URL on your mobile device for quick access
- **Secure Access**: Always logout when finished, especially on shared computers

### What to Expect:
- **Real-Time Updates**: See your progress update as soon as interviews are completed
- **Detailed Timeline**: Complete history of your interview journey with M4 Consulting
- **Professional Presentation**: Clean, professional interface suitable for sharing with references

---

**Welcome to your M4 Consulting Interview Progress Portal!**

We're excited to provide you with this transparent view into your interview process. This portal represents our commitment to keeping you informed every step of the way.

*For technical support or questions about accessing your portal, please contact your M4 Consulting representative.*`;

    const fullInstructions = baseInstructions + candidateUrls + remainingInstructions;

    // Convert markdown to HTML
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>M4 Consulting Candidate Progress Portal - Access Instructions</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .container {
            background-color: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        h1 {
            color: #2c3e50;
            border-bottom: 3px solid #3498db;
            padding-bottom: 10px;
            margin-bottom: 30px;
        }
        h2 {
            color: #34495e;
            margin-top: 30px;
            margin-bottom: 15px;
        }
        h3 {
            color: #5a6c7d;
            margin-top: 25px;
        }
        hr {
            border: none;
            border-top: 2px solid #ecf0f1;
            margin: 30px 0;
        }
        code {
            background-color: #f1f2f6;
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Consolas', monospace;
            font-size: 0.9em;
        }
        .candidate-info {
            background-color: #f8f9ff;
            border-left: 4px solid #3498db;
            padding: 15px;
            margin: 10px 0;
            border-radius: 0 4px 4px 0;
        }
        .candidate-info strong {
            color: #2c3e50;
        }
        ul, ol {
            padding-left: 20px;
        }
        li {
            margin: 8px 0;
        }
        .security-feature {
            background-color: #d5f4e6;
            padding: 10px;
            border-radius: 4px;
            margin: 5px 0;
        }
        .troubleshooting {
            background-color: #fff3cd;
            border: 1px solid #ffeaa7;
            padding: 15px;
            border-radius: 4px;
            margin: 15px 0;
        }
        @media print {
            body { background-color: white; }
            .container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="container">
${fullInstructions
  .replace(/^# (.*$)/gim, '<h1>$1</h1>')
  .replace(/^## (.*$)/gim, '<h2>$1</h2>')
  .replace(/^### (.*$)/gim, '<h3>$1</h3>')
  .replace(/^---$/gm, '<hr>')
  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  .replace(/`([^`]+)`/g, '<code>$1</code>')
  .replace(/^- \*\*(.*?)\*\*:/gm, '<div class="candidate-info"><strong>$1:</strong>')
  .replace(/^ {2}- \*\*(.*?)\*\*: (.*$)/gm, '<br><strong>$1</strong>: $2')
  .replace(/^ {2}- \*\*(.*?)\*\*:$/gm, '<br><strong>$1</strong>:')
  .replace(/^ {4}- `(.*?)`$/gm, '<br>&nbsp;&nbsp;&nbsp;&nbsp;• <code>$1</code>')
  .replace(/^- ✅ (.*$)/gm, '<div class="security-feature">✅ $1</div>')
  .replace(/^- (.*$)/gm, '<li>$1</li>')
  .replace(/^[0-9]+\. (.*$)/gm, '<li>$1</li>')
  .replace(/^([^<\n].*)$/gm, '<p>$1</p>')
  .replace(/<p><\/p>/g, '')
  .replace(/<li>/g, '<ul><li>')
  .replace(/<\/li>(?!\s*<li>)/g, '</li></ul>')
  .replace(/<\/ul>\s*<ul>/g, '')
  .replace(/class="candidate-info"><strong>(.*?)<\/strong>([\s\S]*?)(?=<div class="candidate-info">|$)/g, 'class="candidate-info"><strong>$1</strong>$2</div>')
}
    </div>
</body>
</html>`;

    const dateStamp = new Date().toISOString().split('T')[0];

    // Create and download markdown file
    const mdBlob = new Blob([fullInstructions], { type: 'text/markdown' });
    const mdUrl = window.URL.createObjectURL(mdBlob);
    const mdLink = document.createElement('a');
    mdLink.href = mdUrl;
    mdLink.download = `candidate-portal-instructions-master-list-${dateStamp}.md`;
    document.body.appendChild(mdLink);
    mdLink.click();
    document.body.removeChild(mdLink);
    window.URL.revokeObjectURL(mdUrl);

    // Create and download HTML file
    setTimeout(() => {
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      const htmlUrl = window.URL.createObjectURL(htmlBlob);
      const htmlLink = document.createElement('a');
      htmlLink.href = htmlUrl;
      htmlLink.download = `candidate-portal-instructions-master-list-${dateStamp}.html`;
      document.body.appendChild(htmlLink);
      htmlLink.click();
      document.body.removeChild(htmlLink);
      window.URL.revokeObjectURL(htmlUrl);
    }, 500);

    // Open HTML in new window for preview (similar to markdown)
    setTimeout(() => {
      const htmlWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
      if (htmlWindow) {
        htmlWindow.document.write(htmlContent);
        htmlWindow.document.close();
      }
    }, 1000);
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'Application Submitted': return 'primary';
      case 'Status Change': return 'secondary';
      case 'Interview Scheduled': return 'info';
      case 'Interview Completed': return 'success';
      case 'Feedback Submitted': return 'warning';
      case 'Offer Extended': return 'success';
      case 'Offer Accepted/Declined': return 'secondary';
      case 'Notes Added': return 'default';
      default: return 'default';
    }
  };

  const formatDateTime = (date: Date, time?: string): string => {
    const dateStr = date.toLocaleDateString();
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  // Parse step number from string or number
  const parseStepNumber = (currentStep: string | number): number => {
    if (typeof currentStep === 'number') {
      return Math.max(1, Math.min(20, currentStep));
    }

    if (typeof currentStep === 'string') {
      // Extract number from text like "Step 12" -> 12
      const match = currentStep.match(/Step\s*(\d+)/i);
      if (match && match[1]) {
        const stepNumber = parseInt(match[1], 10);
        return Math.max(1, Math.min(20, stepNumber));
      }
    }

    return 1; // Default fallback
  };

  // Calculate current step for a candidate based on their timeline events
  const getCurrentStepForCandidate = (candidateId: string): number => {
    if (!candidateId) return 1;

    // Get ALL timeline events that might be related to this candidate
    const allRelatedEvents = timelineEvents.filter(event => {
      const matchDirectCandidate = event.candidate?.id === candidateId;
      const matchApplicationCandidate = event.application?.candidateId === candidateId;
      const matchApplicationId = event.applicationId && event.application?.candidateId === candidateId;

      return matchDirectCandidate || matchApplicationCandidate || matchApplicationId;
    });

    if (allRelatedEvents.length === 0) {
      return 1;
    }

    // Find events that have a currentStep value and parse the step number
    const eventsWithSteps = allRelatedEvents
      .filter(event => event.currentStep != null && event.currentStep !== undefined)
      .map(event => ({
        ...event,
        parsedStepNumber: parseStepNumber(event.currentStep!)
      }))
      .filter(event => event.parsedStepNumber > 0)
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());

    if (eventsWithSteps.length > 0) {
      return eventsWithSteps[0].parsedStepNumber;
    }

    return 1;
  };


  // Get the timeline step object for a given step number
  const getTimelineStepByNumber = (stepNumber: number): TimelineStep | null => {
    return interviewTimelineSteps.find(step => step.id === stepNumber) || null;
  };

  // Group timeline events by position
  const groupEventsByPosition = (events: LinkedTimelineEvent[]) => {
    const grouped: { [key: string]: LinkedTimelineEvent[] } = {};

    events.forEach(event => {
      const positionTitle = event.position?.title || 'Unknown Position';

      if (!grouped[positionTitle]) {
        grouped[positionTitle] = [];
      }

      grouped[positionTitle].push(event);
    });

    return grouped;
  };

  // Calculate average progress across all candidates
  const calculateAverageProgress = (): { averageStep: number; progressPercentage: number } => {
    // Get unique candidates from timeline events
    const uniqueCandidates = Array.from(
      new Set(
        timelineEvents
          .filter(event => event.candidate?.id)
          .map(event => event.candidate!.id)
      )
    );

    if (uniqueCandidates.length === 0) {
      return { averageStep: 1, progressPercentage: 5 }; // Default values
    }

    // Calculate current step for each candidate
    const candidateSteps = uniqueCandidates.map(candidateId =>
      getCurrentStepForCandidate(candidateId)
    );

    // Calculate average
    const totalSteps = candidateSteps.reduce((sum, step) => sum + step, 0);
    const averageStep = Math.round(totalSteps / candidateSteps.length);
    const progressPercentage = Math.round((averageStep / 20) * 100);

    return { averageStep, progressPercentage };
  };

  // Email Template Variables Helper


  // Get responsive step configuration
  const getStepsPerView = (): number => {
    if (isSmall) return 5; // Small screens: 5 steps
    if (isMedium) return 10; // Medium screens: 10 steps
    return 20; // Large screens: all 20 steps
  };

  // Group steps for responsive display
  const getStepsToDisplay = (): TimelineStep[][] => {
    const stepsPerView = getStepsPerView();
    const groups: TimelineStep[][] = [];

    for (let i = 0; i < interviewTimelineSteps.length; i += stepsPerView) {
      groups.push(interviewTimelineSteps.slice(i, i + stepsPerView));
    }

    return groups;
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Box display="flex" alignItems="center" gap={2}>
          <TimelineIcon sx={{ color: '#60a5fa', fontSize: '2rem' }} />
          <Typography
            variant="h4"
            className="title-gradient"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Candidate Updates
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            size="small"
            label="Report Date"
            InputLabelProps={{ shrink: true }}
          />
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<TextFormatIcon />}
            onClick={handleTextFormat}
            sx={{
              borderColor: 'rgba(96, 165, 250, 0.5)',
              color: '#60a5fa',
              '&:hover': {
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(96, 165, 250, 0.1)'
              }
            }}
          >
            Basic Text Format
          </Button>
          <Button
            variant="outlined"
            startIcon={<HtmlIcon />}
            onClick={handleHTMLFormat}
            sx={{
              borderColor: 'rgba(52, 211, 153, 0.5)',
              color: '#34d399',
              '&:hover': {
                borderColor: '#34d399',
                backgroundColor: 'rgba(52, 211, 153, 0.1)'
              }
            }}
          >
            HTML Email Format
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={generatePortalInstructions}
            sx={{
              borderColor: 'rgba(96, 165, 250, 0.5)',
              color: '#60a5fa',
              '&:hover': {
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(96, 165, 250, 0.1)'
              }
            }}
          >
            Generate Portal Instructions
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Interview Process Timeline Chart */}
      <Card elevation={3} sx={{ mb: 4, overflow: 'visible' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <TimelineIcon sx={{ color: '#60a5fa', fontSize: '1.5rem', mr: 2 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              20-Step Interview Process Timeline
            </Typography>
          </Box>

          {/* Timeline Progress Bar */}
          <Box sx={{ mb: 4, position: 'relative' }}>
            {(() => {
              const { averageStep, progressPercentage } = calculateAverageProgress();
              return (
                <>
                  <Box
                    sx={{
                      height: 24,
                      background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
                      borderRadius: 4,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${progressPercentage}%`,
                        background: averageStep >= 20
                          ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)'
                          : 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                        borderRadius: 4,
                        position: 'relative',
                        transition: 'width 0.5s ease',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                          animation: 'shimmer 2s infinite'
                        }
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 12,
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.8rem'
                    }}
                  >
                    Average: Step {averageStep}/20 ({progressPercentage}%)
                  </Typography>
                </>
              );
            })()}
          </Box>

          {/* Responsive Timeline Steps Grid */}
          <Box sx={{ pb: 2 }}>
            {getStepsToDisplay().map((stepGroup, groupIndex) => (
              <Box key={groupIndex} sx={{ mb: groupIndex < getStepsToDisplay().length - 1 ? 4 : 0 }}>
                {/* Group Header */}
                {getStepsPerView() < 20 && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }}
                    >
                      Steps {stepGroup[0].id} - {stepGroup[stepGroup.length - 1].id}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ overflowX: 'auto', pb: 2 }}>
                  <Box sx={{
                    display: 'flex',
                    minWidth: isSmall ? '300px' : isMedium ? '600px' : '1200px',
                    gap: 1
                  }}>
                    {stepGroup.map((step, stepIndex) => {
                      const { averageStep } = calculateAverageProgress();
                      // For steps 8-17 (strategic interviews), only mark as completed if they're administrative type (steps 1-7)
                      // This ensures strategic interviews (8-17) show blue background even if average progress suggests completion
                      const isCompleted = step.id <= averageStep && step.id <= 7;
                      const stepToRender = { ...step, completed: isCompleted };

                      return (
                        <Box
                          key={step.id}
                          sx={{
                            flex: `0 0 ${(100 / getStepsPerView())}%`,
                            position: 'relative',
                            mb: 2
                          }}
                        >
                          {/* Step Circle */}
                          <Box
                            sx={{
                              width: isSmall ? 32 : 40,
                              height: isSmall ? 32 : 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto 8px auto',
                              background: (step.id >= 11 && step.id <= 12)
                                ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'  // Light gray for Assessment steps (priority over all)
                                : (step.id >= 5 && step.id <= 7)
                                ? 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)'  // Cyan for ESP interviews (priority over completion)
                                : stepToRender.completed
                                ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                                : stepToRender.type === 'final'
                                ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                                : stepToRender.type === 'interview'
                                ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                                : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                              border: `2px solid ${(step.id >= 11 && step.id <= 12)
                                ? '#9ca3af'  // Light gray border for Assessment steps (priority over all)
                                : (step.id >= 5 && step.id <= 7)
                                ? '#06b6d4'  // Cyan border for ESP interviews (priority over completion)
                                : stepToRender.completed
                                ? '#10b981'
                                : stepToRender.type === 'final'
                                ? '#f59e0b'
                                : stepToRender.type === 'interview'
                                ? '#3b82f6'
                                : '#6b7280'
                              }`,
                              boxShadow: `0 4px 12px ${(step.id >= 11 && step.id <= 12)
                                ? 'rgba(156, 163, 175, 0.3)'  // Light gray shadow for Assessment steps (priority over all)
                                : (step.id >= 5 && step.id <= 7)
                                ? 'rgba(6, 182, 212, 0.3)'  // Cyan shadow for ESP interviews (priority over completion)
                                : stepToRender.completed
                                ? 'rgba(16, 185, 129, 0.3)'
                                : stepToRender.type === 'final'
                                ? 'rgba(245, 158, 11, 0.3)'
                                : stepToRender.type === 'interview'
                                ? 'rgba(59, 130, 246, 0.3)'
                                : 'rgba(107, 114, 128, 0.3)'
                              }`,
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: `0 6px 20px ${(step.id >= 5 && step.id <= 7)
                                  ? 'rgba(6, 182, 212, 0.4)'  // Cyan hover shadow for ESP interviews (priority over completion)
                                  : stepToRender.completed
                                  ? 'rgba(16, 185, 129, 0.4)'
                                  : stepToRender.type === 'final'
                                  ? 'rgba(245, 158, 11, 0.4)'
                                  : stepToRender.type === 'interview'
                                  ? 'rgba(59, 130, 246, 0.4)'
                                  : 'rgba(107, 114, 128, 0.4)'
                                }`
                              }
                            }}
                          >
                            <Box sx={{
                              color: 'white',
                              fontSize: isSmall ? '1rem' : '1.2rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {stepToRender.icon}
                            </Box>
                          </Box>

                          {/* Step Details */}
                          <Box sx={{ textAlign: 'center', px: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: (step.id >= 11 && step.id <= 12)
                                  ? '#9ca3af'  // Light gray text for Assessment steps (priority over all)
                                  : (step.id >= 5 && step.id <= 7)
                                  ? '#06b6d4'  // Cyan text for ESP interviews (priority over completion)
                                  : stepToRender.completed
                                  ? '#10b981'
                                  : '#ffffff',
                                fontWeight: 600,
                                fontSize: isSmall ? '0.6rem' : '0.7rem',
                                display: 'block',
                                mb: 0.5
                              }}
                            >
                              Step {stepToRender.id}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: isSmall ? '0.6rem' : '0.65rem',
                                display: 'block',
                                lineHeight: 1.2,
                                fontWeight: 500
                              }}
                            >
                              {stepToRender.title}
                            </Typography>
                            <Chip
                              label={
                                stepToRender.id === 11 || stepToRender.id === 12 ? 'Prefinal' :
                                stepToRender.type === 'interview' ? 'Interview' :
                                stepToRender.type === 'administrative' ? 'Admin' :
                                'Final'
                              }
                              size="small"
                              sx={{
                                mt: 0.5,
                                height: isSmall ? 14 : 16,
                                fontSize: isSmall ? '0.5rem' : '0.6rem',
                                backgroundColor: stepToRender.id === 11 || stepToRender.id === 12
                                  ? 'rgba(16, 185, 129, 0.2)'  // Green background for prefinal steps
                                  : stepToRender.type === 'interview'
                                  ? 'rgba(59, 130, 246, 0.2)'
                                  : stepToRender.type === 'administrative'
                                  ? 'rgba(107, 114, 128, 0.2)'
                                  : 'rgba(245, 158, 11, 0.2)',
                                color: stepToRender.id === 11 || stepToRender.id === 12
                                  ? '#10b981'  // Green text for prefinal steps
                                  : stepToRender.type === 'interview'
                                  ? '#60a5fa'
                                  : stepToRender.type === 'administrative'
                                  ? '#9ca3af'
                                  : '#fbbf24'
                              }}
                            />
                          </Box>

                          {/* Connecting Line (only within group) */}
                          {stepIndex < stepGroup.length - 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: isSmall ? 16 : 20,
                                right: '-2px',
                                width: '100%',
                                height: 2,
                                background: stepToRender.completed && (step.id + 1) <= averageStep
                                  ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                                  : 'linear-gradient(90deg, rgba(107, 114, 128, 0.3) 0%, rgba(156, 163, 175, 0.3) 100%)',
                                zIndex: -1
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Connection between groups */}
                {groupIndex < getStepsToDisplay().length - 1 && (
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        background: 'linear-gradient(180deg, rgba(107, 114, 128, 0.5) 0%, rgba(156, 163, 175, 0.5) 100%)',
                        margin: '0 auto',
                        borderRadius: 2
                      }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Legend */}
          <Box display="flex" justifyContent="center" gap={3} mt={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Interview Steps (14 total)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Administrative Steps (5 total)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Final Meeting (1 total)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Report Header for Print */}
      <Box className="print-only" sx={{ mb: 4, textAlign: 'center', display: 'none' }}>
        <Typography variant="h3" sx={{ color: '#000', mb: 1 }}>
          M4 Consulting - Candidate Updates
        </Typography>
        <Typography variant="h6" sx={{ color: '#666' }}>
          Report Date: {new Date().toLocaleDateString()}
        </Typography>
      </Box>

      {/* Timeline Events Grouped by Position */}
      <Box>
        {Object.entries(groupEventsByPosition(timelineEvents)).map(([positionTitle, events]) => (
          <Box key={positionTitle} mb={4}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                borderBottom: '2px solid',
                borderColor: '#60a5fa',
                pb: 1,
                mb: 3,
                color: '#ffffff',
              }}
            >
              POSITION: {positionTitle}
            </Typography>

            <Grid container spacing={3}>
              {events.map((event) => (
          <Grid item xs={12} md={6} lg={4} key={event.id}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" gap={2} alignItems="flex-start" flex={1}>
                    {/* Candidate Image */}
                    {event.candidateImage && event.candidateImage.length > 0 && (
                      <Box
                        component="img"
                        src={event.candidateImage[0].url}
                        alt={event.candidate
                          ? `${event.candidate.firstName} ${event.candidate.lastName}`
                          : 'Candidate'
                        }
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          objectFit: 'cover',
                          border: '2px solid rgba(96, 165, 250, 0.3)',
                          flexShrink: 0
                        }}
                      />
                    )}

                    <Box flex={1}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ color: '#ffffff', fontWeight: 600 }}
                      >
                        {event.candidate
                          ? `${event.candidate.firstName} ${event.candidate.lastName}`
                          : 'Candidate TBD'
                        }
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}
                      >
                        {event.position?.title || 'Position TBD'}
                      </Typography>
                      {/* Debug: Show in UI status */}
                      <Chip
                        label={`Show in UI: ${event.candidate?.showInUI === false ? 'NO' : 'YES'}`}
                        size="small"
                        sx={{
                          backgroundColor: event.candidate?.showInUI === false ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                          color: event.candidate?.showInUI === false ? '#ef4444' : '#22c55e',
                          fontWeight: 500,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Chip
                    label={event.eventType}
                    color={getEventTypeColor(event.eventType) as any}
                    size="small"
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    mb: 1
                  }}
                >
                  {formatDateTime(event.eventDate, event.eventTime)}
                </Typography>

                {/* Individual Candidate Timeline Progress */}
                {event.candidate && (
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#60a5fa', fontWeight: 500, fontSize: '0.8rem' }}
                      >
                        Interview Progress
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}
                      >
                        Step {getCurrentStepForCandidate(event.candidate.id)}/20
                      </Typography>
                    </Box>

                    {/* Mini Progress Bar */}
                    <Box sx={{ position: 'relative', mb: 1 }}>
                      <Box
                        sx={{
                          height: 12,
                          background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
                          borderRadius: 2,
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(getCurrentStepForCandidate(event.candidate.id) / 20) * 100}%`,
                            background: getCurrentStepForCandidate(event.candidate.id) >= 20
                              ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
                              : 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                            borderRadius: 2,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Current Step Info */}
                    {(() => {
                      const currentStep = getCurrentStepForCandidate(event.candidate.id);
                      const stepInfo = getTimelineStepByNumber(currentStep);
                      return stepInfo ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: stepInfo.type === 'final'
                                ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                                : stepInfo.type === 'interview'
                                ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                                : stepInfo.type === 'assessment'
                                ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                              fontSize: '0.7rem'
                            }}
                          >
                            <Box sx={{ fontSize: '12px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {stepInfo.icon}
                            </Box>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}
                          >
                            {stepInfo.title}
                          </Typography>
                          <Chip
                            label={
                              stepInfo.id === 11 || stepInfo.id === 12 ? 'Prefinal' :
                              stepInfo.type === 'interview' ? 'Interview' :
                              stepInfo.type === 'administrative' ? 'Admin' :
                              stepInfo.type === 'assessment' ? 'Assessment' :
                              'Final'
                            }
                            size="small"
                            sx={{
                              height: 14,
                              fontSize: '0.6rem',
                              backgroundColor: stepInfo.id === 11 || stepInfo.id === 12
                                ? 'rgba(16, 185, 129, 0.2)'  // Green background for prefinal steps
                                : stepInfo.type === 'interview'
                                ? 'rgba(59, 130, 246, 0.2)'
                                : stepInfo.type === 'administrative'
                                ? 'rgba(107, 114, 128, 0.2)'
                                : stepInfo.type === 'assessment'
                                ? 'rgba(156, 163, 175, 0.2)'
                                : 'rgba(245, 158, 11, 0.2)',
                              color: stepInfo.id === 11 || stepInfo.id === 12
                                ? '#10b981'  // Green text for prefinal steps
                                : stepInfo.type === 'interview'
                                ? '#60a5fa'
                                : stepInfo.type === 'administrative'
                                ? '#9ca3af'
                                : stepInfo.type === 'assessment'
                                ? '#9ca3af'
                                : '#fbbf24'
                            }}
                          />
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                )}


                {event.previousStatus && event.newStatus && (
                  <Box mb={2}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#60a5fa', fontWeight: 500 }}
                    >
                      Status Change:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip label={event.previousStatus} size="small" variant="outlined" />
                      <Typography sx={{ color: '#60a5fa' }}>→</Typography>
                      <Chip label={event.newStatus} size="small" />
                    </Box>
                  </Box>
                )}


                <Typography
                  variant="body2"
                  paragraph
                  sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong style={{ color: '#60a5fa' }}>Update:</strong> {event.description}
                </Typography>

                {event.notes && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#60a5fa' }}>Notes:</strong> {event.notes}
                  </Typography>
                )}

                {event.nextSteps && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#60a5fa' }}>Next Steps:</strong> {event.nextSteps}
                  </Typography>
                )}

                {event.availableMeetTimes && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#93c5fd' }}>Available Meet Times:</strong> {event.availableMeetTimes}
                  </Typography>
                )}

                {/* Candidate Questions & Admin Answers */}
                {(event.candidateQuestions || event.adminAnswers) && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: 2,
                      p: 2,
                      my: 2
                    }}
                  >
                    {event.candidateQuestions && (
                      <Box mb={event.adminAnswers ? 2 : 0}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          💬 Candidate Question:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            backgroundColor: 'rgba(96, 165, 250, 0.05)',
                            p: 1.5,
                            borderRadius: 1,
                            fontStyle: 'italic'
                          }}
                        >
                          "{event.candidateQuestions}"
                        </Typography>
                      </Box>
                    )}

                    {event.adminAnswers && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#34d399',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          ✅ Admin Response:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            backgroundColor: 'rgba(52, 211, 153, 0.05)',
                            p: 1.5,
                            borderRadius: 1
                          }}
                        >
                          {event.adminAnswers}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Typical Candidate Questions & Admin Answers */}
                {(event.typicalCandidateQuestions || event.typicalAdminAnswers) && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(139, 69, 19, 0.1)', // Brown tint to distinguish from dynamic Q&A
                      border: '1px solid rgba(139, 69, 19, 0.3)',
                      borderRadius: 2,
                      p: 2,
                      my: 2
                    }}
                  >
                    {event.typicalCandidateQuestions && (
                      <Box mb={event.typicalAdminAnswers ? 2 : 0}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <PsychologyIcon sx={{ fontSize: '1.2rem' }} />
                          Key Candidate Question:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                            backgroundColor: 'rgba(51, 65, 85, 0.95)',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(148, 163, 184, 0.4)',
                            fontWeight: 500
                          }}
                        >
                          "{event.typicalCandidateQuestions}"
                        </Typography>
                      </Box>
                    )}

                    {event.typicalAdminAnswers && (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#34d399',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <QuestionAnswerIcon sx={{ fontSize: '1.2rem' }} />
                          Key Candidate Answer:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ffffff',
                            lineHeight: 1.6,
                            backgroundColor: 'rgba(51, 65, 85, 0.95)',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(148, 163, 184, 0.4)',
                            fontWeight: 500,
                            fontSize: 'calc(0.875rem - 2pt)'
                          }}
                        >
                          {event.typicalAdminAnswers}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    Created by: {event.createdBy}
                  </Typography>
                  {event.automatedEvent && (
                    <Chip
                      label="Auto"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>

      {timelineEvents.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <TimelineIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            No candidate updates found
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Timeline events will appear here as they are created.
          </Typography>
        </Box>
      )}

      {/* Print Styles */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body {
            font-size: 12px;
          }
          .MuiCard-root {
            box-shadow: none !important;
            border: 1px solid #ddd;
            break-inside: avoid;
          }
        }
      `}</style>
    </Box>
  );
};

export default CandidateUpdates;